import React, { useState } from 'react';
import * as XLSX from 'xlsx';

const BulkExport = ({ onClose, isDarkMode, batchGroups, allContacts, visibleColumns, columnOrder, filteredContacts }) => {
  const [selectedBatches, setSelectedBatches] = useState(new Set());
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [exportMode, setExportMode] = useState('batches'); // 'batches', 'individual' nebo 'filtered'
  const [exportColumns, setExportColumns] = useState(visibleColumns || {});
  const [isExporting, setIsExporting] = useState(false);

  // Toggle batch selection
  const toggleBatch = (batchName) => {
    const newSelected = new Set(selectedBatches);
    if (newSelected.has(batchName)) {
      newSelected.delete(batchName);
    } else {
      newSelected.add(batchName);
    }
    setSelectedBatches(newSelected);
  };

  // Toggle individual contact selection
  const toggleContact = (contactId) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
  };

  // Toggle column for export
  const toggleExportColumn = (columnName) => {
    setExportColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  // Select all batches
  const selectAllBatches = () => {
    setSelectedBatches(new Set(Object.keys(batchGroups)));
  };

  // Clear all batch selections
  const clearAllBatches = () => {
    setSelectedBatches(new Set());
  };

  // Select all contacts
  const selectAllContacts = () => {
    setSelectedContacts(new Set(allContacts.map(c => c._id)));
  };

  // Clear all contact selections
  const clearAllContacts = () => {
    setSelectedContacts(new Set());
  };

  // Get contacts to export based on mode
  const getContactsToExport = () => {
    if (exportMode === 'batches') {
      const contacts = [];
      selectedBatches.forEach(batchName => {
        const batchContacts = batchGroups[batchName] || [];
        contacts.push(...batchContacts);
      });
      return contacts;
    } else if (exportMode === 'filtered') {
      return filteredContacts || [];
    } else {
      return allContacts.filter(contact => selectedContacts.has(contact._id));
    }
  };

  // Export to Excel
  const handleExport = async () => {
    const contactsToExport = getContactsToExport();
    
    if (contactsToExport.length === 0) {
      alert('Vyberte alespoň jeden kontakt nebo šarži k exportu.');
      return;
    }

    const selectedColumnList = Object.entries(exportColumns)
      .filter(([, isSelected]) => isSelected)
      .map(([columnName]) => columnName);

    if (selectedColumnList.length === 0) {
      alert('Vyberte alespoň jeden sloupec k exportu.');
      return;
    }

    setIsExporting(true);

    try {
      // Prepare data for export
      const exportData = contactsToExport.map(contact => {
        const row = {};
        
        selectedColumnList.forEach(columnName => {
          switch (columnName) {
            case 'name':
              row['Název'] = contact.name || '';
              break;
            case 'ico':
              row['IČO'] = contact.ico || '';
              break;
            case 'address':
              row['Adresa'] = contact.address || '';
              break;
            case 'operator':
              row['Operátor'] = contact.operator || '';
              break;
            case 'status':
              row['Stav'] = contact.status || '';
              break;
            case 'contact':
              row['Kontakt'] = contact.contacts?.[0]?.name || '';
              break;
            case 'phone':
              row['Telefon'] = contact.contacts?.[0]?.phone || '';
              break;
            case 'email':
              row['Email'] = contact.contacts?.[0]?.email || '';
              break;
            case 'bmsl':
              row['BMSL'] = contact.bmsl || '';
              break;
            case 'forecast':
              row['Forecast'] = contact.forecast || '';
              break;
            case 'ca':
              row['CA'] = contact.ca || '';
              break;
            case 'executive':
              // Použít createdBy informace místo executive pole
              let executiveName = '';
              if (typeof contact.createdBy === 'object' && contact.createdBy?.name) {
                executiveName = contact.createdBy.name;
              } else if (typeof contact.createdBy === 'string') {
                // Pokud je createdBy jen ID, zkusit najít jméno v ownerInfo
                executiveName = contact.ownerInfo?.username || '';
              }
              row['Vlastník firmy CRM'] = executiveName;
              break;
            case 'prm':
              row['PRM Vodafone'] = contact.prm || '';
              break;
            case 'closedDate':
              row['Zobchodováno'] = contact.closedDate || '';
              break;
            case 'contractLength':
              row['Délka smlouvy'] = contact.contractLength || '';
              break;
            case 'personType':
              row['Typ osoby'] = contact.personType || '';
              break;
            case 'nextActionDate':
              row['Datum další akce'] = contact.nextActionDate ? new Date(contact.nextActionDate).toLocaleDateString('cs-CZ') : '';
              break;
            case 'owner':
              row['Vlastník'] = contact.ownerInfo?.username || '';
              break;
            default:
              break;
          }
        });
        
        return row;
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Kontakty');
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const modeText = exportMode === 'batches' ? 'sarze' : 
                      exportMode === 'filtered' ? 'vyfiltrovane' : 'vybrane';
      const filename = `export-kontakty-${modeText}-${timestamp}.xlsx`;
      
      // Save file
      XLSX.writeFile(workbook, filename);
      
      alert(`Export dokončen! Exportováno ${contactsToExport.length} kontaktů do souboru ${filename}.`);
      onClose();
      
    } catch (error) {
      console.error('Chyba při exportu:', error);
      alert('Chyba při exportu: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const columnLabels = {
    name: '📋 Název',
    ico: '🏢 IČO',
    address: '📍 Adresa',
    operator: '📱 Operátor',
    status: '🏷️ Stav',
    contact: '👤 Kontakt',
    phone: '☎️ Telefon',
    email: '✉️ Email',
    bmsl: '💰 BMSL',
    forecast: '📊 Forecast',
    ca: '🆔 CA',
    executive: '👤 Vlastník firmy CRM',
    prm: '📞 PRM Vodafone',
    closedDate: '🎯 Zobchodováno',
    contractLength: '📅 Délka smlouvy',
    personType: '🏷️ Typ osoby',
    nextActionDate: '📅 Datum další akce',
    owner: '👤 Vlastník'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            🚀 Hromadný export kontaktů
          </h2>
          <button
            onClick={onClose}
            className={`px-3 py-1 rounded ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            }`}
          >
            ✕
          </button>
        </div>

        {/* Export mode selection */}
        <div className="mb-6">
          <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Režim exportu
          </h3>
          <div className="flex gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                checked={exportMode === 'batches'}
                onChange={() => setExportMode('batches')}
                className="rounded"
              />
              <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Exportovat celé šarže</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                checked={exportMode === 'individual'}
                onChange={() => setExportMode('individual')}
                className="rounded"
              />
              <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Exportovat pouze označené kontakty</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                checked={exportMode === 'filtered'}
                onChange={() => setExportMode('filtered')}
                className="rounded"
              />
              <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Exportovat vyfiltrované ({filteredContacts?.length || 0} kontaktů)
              </span>
            </label>
          </div>
        </div>

        {/* Batch selection */}
        {exportMode === 'batches' && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Výběr šarží ({selectedBatches.size} vybraných)
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={selectAllBatches}
                  className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-sm"
                >
                  Vybrat vše
                </button>
                <button
                  onClick={clearAllBatches}
                  className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-sm"
                >
                  Zrušit výběr
                </button>
              </div>
            </div>
            
            <div className={`border rounded p-4 max-h-48 overflow-y-auto ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
              {Object.entries(batchGroups).map(([batchName, contacts]) => (
                <label key={batchName} className="flex items-center space-x-2 mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedBatches.has(batchName)}
                    onChange={() => toggleBatch(batchName)}
                    className="rounded"
                  />
                  <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {batchName} ({contacts.length} kontaktů)
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Individual contact selection */}
        {exportMode === 'individual' && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Výběr kontaktů ({selectedContacts.size} vybraných)
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={selectAllContacts}
                  className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-sm"
                >
                  Vybrat vše
                </button>
                <button
                  onClick={clearAllContacts}
                  className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-sm"
                >
                  Zrušit výběr
                </button>
              </div>
            </div>
            
            <div className={`border rounded p-4 max-h-48 overflow-y-auto ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
              {allContacts.map(contact => (
                <label key={contact._id} className="flex items-center space-x-2 mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedContacts.has(contact._id)}
                    onChange={() => toggleContact(contact._id)}
                    className="rounded"
                  />
                  <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {contact.name} - {contact.ico || 'Bez IČO'}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Column selection */}
        <div className="mb-6">
          <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Sloupce k exportu ({Object.values(exportColumns).filter(Boolean).length} vybraných)
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {columnOrder.map(columnName => (
              <label key={columnName} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exportColumns[columnName] || false}
                  onChange={() => toggleExportColumn(columnName)}
                  className="rounded"
                />
                <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {columnLabels[columnName] || columnName}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Export summary */}
        <div className={`p-3 rounded mb-6 ${isDarkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
          <h4 className={`font-medium ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>Souhrn exportu:</h4>
          <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
            {exportMode === 'batches' 
              ? `Vybraných šarží: ${selectedBatches.size}, Kontaktů k exportu: ${getContactsToExport().length}`
              : `Vybraných kontaktů: ${selectedContacts.size}`
            }
          </p>
          <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
            Sloupců k exportu: {Object.values(exportColumns).filter(Boolean).length}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isExporting}
            className={`px-4 py-2 rounded ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Zrušit
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || getContactsToExport().length === 0 || Object.values(exportColumns).filter(Boolean).length === 0}
            className={`px-4 py-2 rounded text-white ${
              isExporting || getContactsToExport().length === 0 || Object.values(exportColumns).filter(Boolean).length === 0
                ? 'bg-gray-500 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isExporting ? '🔄 Exportuji...' : '📤 Exportovat do Excel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkExport;
